import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import ExpedienteDetalle from './ExpedienteDetalle';
import { onEntidadChanged } from '../utils/entidadBus';
import { onDataChanged } from '../utils/dataBus';
import { localTodayRange } from '../utils/dateUtils';
import ProductividadPanel from './ProductividadPanel';

export default function Dashboard({ onNavigate }) {
  const [stats, setStats] = useState({ clientes: 0, expedientes: 0, tramites: 0, ingresosMes: 0, gastosMes: 0, deudaPendiente: 0 });
  const [actividadHoy, setActividadHoy] = useState({
    tareasCreadas: 0, tareasCompletadas: 0, clientesNuevos: 0,
    expedientesNuevos: 0, tramitesNuevos: 0, eventosCreados: 0, audienciasRealizadas: 0,
  });
  const [eventosHoy, setEventosHoy] = useState([]);
  const [proximosEventos, setProximosEventos] = useState([]);
  const [tareasPendientes, setTareasPendientes] = useState([]);
  const [ingresosPorMes, setIngresosPorMes] = useState([]);
  const [deudasMes, setDeudasMes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contableOpen, setContableOpen] = useState(false);
  const [verExpediente, setVerExpediente] = useState(null);

  const today = new Date().toISOString().split('T')[0];
  const mesActual = new Date().toISOString().substring(0, 7);

  useEffect(() => { fetchDashboard(); fetchActividadHoy(); }, []);

  // Mantenerse sincronizado cuando un expediente/trámite/cliente cambia desde otro lugar
  useEffect(() => onEntidadChanged(() => { fetchDashboard(); fetchActividadHoy(); }), []);

  // Automático: cualquier escritura sobre las tablas que alimentan el panel
  useEffect(() => onDataChanged(() => { fetchDashboard(); fetchActividadHoy(); }, ['expedientes', 'tramites', 'clientes', 'agenda', 'tareas', 'ingresos', 'gastos', 'deudas']), []);

  // Cuánto se trabajó hoy: cuenta lo creado/completado en el día local, no
  // en UTC (importante después de las 21hs en Argentina).
  async function fetchActividadHoy() {
    const { inicio, fin } = localTodayRange();
    const [
      { count: tareasCreadas },
      { count: tareasCompletadas },
      { count: clientesNuevos },
      { count: expedientesNuevos },
      { count: tramitesNuevos },
      { count: eventosCreados },
      { count: audienciasRealizadas },
    ] = await Promise.all([
      supabase.from('tareas').select('*', { count: 'exact', head: true })
        .gte('created_at', inicio).lt('created_at', fin).eq('eliminado', false),
      supabase.from('tareas').select('*', { count: 'exact', head: true })
        .gte('completada_en', inicio).lt('completada_en', fin).eq('eliminado', false),
      supabase.from('clientes').select('*', { count: 'exact', head: true })
        .gte('created_at', inicio).lt('created_at', fin).eq('eliminado', false),
      supabase.from('expedientes').select('*', { count: 'exact', head: true })
        .gte('created_at', inicio).lt('created_at', fin).eq('eliminado', false),
      supabase.from('tramites').select('*', { count: 'exact', head: true })
        .gte('created_at', inicio).lt('created_at', fin).eq('eliminado', false),
      supabase.from('agenda').select('*', { count: 'exact', head: true })
        .gte('created_at', inicio).lt('created_at', fin).eq('eliminado', false),
      supabase.from('agenda').select('*', { count: 'exact', head: true })
        .eq('fecha', today).eq('estado', 'realizado').eq('eliminado', false),
    ]);
    setActividadHoy({
      tareasCreadas: tareasCreadas || 0,
      tareasCompletadas: tareasCompletadas || 0,
      clientesNuevos: clientesNuevos || 0,
      expedientesNuevos: expedientesNuevos || 0,
      tramitesNuevos: tramitesNuevos || 0,
      eventosCreados: eventosCreados || 0,
      audienciasRealizadas: audienciasRealizadas || 0,
    });
  }

  async function fetchDashboard() {
    setLoading(true);
    const [
      { count: totalClientes },
      { count: totalExpedientes },
      { count: totalTramites },
      { data: ingresos },
      { data: gastos },
      { data: deudas },
      { data: agenda },
      { data: tareas }
    ] = await Promise.all([
      supabase.from('clientes').select('*', { count: 'exact', head: true }).eq('eliminado', false),
      supabase.from('expedientes').select('*', { count: 'exact', head: true }).eq('eliminado', false),
      supabase.from('tramites').select('*', { count: 'exact', head: true }).neq('estado', 'completado').eq('eliminado', false),
      supabase.from('ingresos').select('monto, fecha').eq('eliminado', false),
      supabase.from('gastos').select('monto, fecha').eq('eliminado', false),
      supabase.from('deudas').select('monto_total, monto_cobrado, fecha_vencimiento, estado, concepto, deudor_nombre, clientes(nombre, apellido)').eq('eliminado', false),
      supabase.from('agenda').select('*, clientes(nombre, apellido), expedientes(caratula)').gte('fecha', today).order('fecha').limit(8).eq('eliminado', false),
      supabase.from('tareas').select('*, expedientes(caratula), tramites(nombre)').eq('completada', false).eq('eliminado', false).order('created_at').limit(8)
    ]);

    const ingresosMes = (ingresos || []).filter(i => i.fecha?.startsWith(mesActual)).reduce((s, i) => s + (i.monto || 0), 0);
    const gastosMes = (gastos || []).filter(g => g.fecha?.startsWith(mesActual)).reduce((s, g) => s + (g.monto || 0), 0);
    const deudaPendiente = (deudas || []).reduce((s, d) => s + ((d.monto_total || 0) - (d.monto_cobrado || 0)), 0);

    const ultimos6 = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(); d.setMonth(d.getMonth() - i);
      const mes = d.toISOString().substring(0, 7);
      const label = d.toLocaleDateString('es-AR', { month: 'short', year: '2-digit' });
      const total = (ingresos || []).filter(ing => ing.fecha?.startsWith(mes)).reduce((s, ing) => s + (ing.monto || 0), 0);
      ultimos6.push({ mes, label, total });
    }

    const deudasDelMes = (deudas || []).filter(d => d.fecha_vencimiento?.startsWith(mesActual));

    setStats({ clientes: totalClientes || 0, expedientes: totalExpedientes || 0, tramites: totalTramites || 0, ingresosMes, gastosMes, deudaPendiente });
    setEventosHoy((agenda || []).filter(e => e.fecha === today));
    setProximosEventos((agenda || []).filter(e => e.fecha > today).slice(0, 5));
    setTareasPendientes(tareas || []);
    setIngresosPorMes(ultimos6);
    setDeudasMes(deudasDelMes);
    setLoading(false);
  }

  const formatMoney = (n) => '$' + (n || 0).toLocaleString('es-AR');
  const formatDate = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('es-AR') : '—';
  const maxIngreso = Math.max(...ingresosPorMes.map(m => m.total), 1);

  const colorTipo = (tipo) => {
    const map = { 'Audiencia': '#ef4444', 'Vencimiento procesal': '#f59e0b', 'Consulta / Turno': '#10b981', 'Reunión': '#3b82f6', 'Vencimiento de deuda': '#f59e0b' };
    return map[tipo] || '#9ca3af';
  };

  if (loading) return (
    <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <span style={{ color: 'var(--text4)' }}>Cargando dashboard...</span>
    </div>
  );

  return (
    <div className="page">
      <div className="page-topbar">
        <div>
          <div className="page-title">Panel general</div>
          <div style={{ fontSize: 12, color: 'var(--text4)', marginTop: 2 }}>
            {new Date().toLocaleDateString('es-AR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={fetchDashboard}>↻ Actualizar</button>
      </div>

      <div className="page-content">
        {/* STATS */}
        <div className="stats-grid stats-grid-4" style={{ marginBottom: 20 }}>
          <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => onNavigate('clientes')}>
            <div className="stat-accent" style={{ background: 'var(--blue)' }}></div>
            <div className="stat-label">Clientes</div>
            <div className="stat-value">{stats.clientes}</div>
            <div className="stat-sub">registrados →</div>
          </div>
          <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => onNavigate('expedientes')}>
            <div className="stat-accent" style={{ background: '#7c3aed' }}></div>
            <div className="stat-label">Expedientes</div>
            <div className="stat-value">{stats.expedientes}</div>
            <div className="stat-sub">en curso →</div>
          </div>
          <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => onNavigate('tramites')}>
            <div className="stat-accent" style={{ background: '#f59e0b' }}></div>
            <div className="stat-label">Trámites Extrajudiciales</div>
            <div className="stat-value">{stats.tramites}</div>
            <div className="stat-sub">pendientes →</div>
          </div>
          <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => onNavigate('agenda')}>
            <div className="stat-accent" style={{ background: '#10b981' }}></div>
            <div className="stat-label">Eventos hoy</div>
            <div className="stat-value">{eventosHoy.length}</div>
            <div className="stat-sub">en agenda →</div>
          </div>
        </div>

        {/* ACTIVIDAD DE HOY */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <span className="card-title">📊 Actividad de hoy</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 1, background: 'var(--border)' }}>
            {[
              { label: 'Tareas creadas', value: actividadHoy.tareasCreadas, color: 'var(--blue)' },
              { label: 'Tareas completadas', value: actividadHoy.tareasCompletadas, color: 'var(--green)' },
              { label: 'Clientes nuevos', value: actividadHoy.clientesNuevos, color: '#7c3aed' },
              { label: 'Expedientes nuevos', value: actividadHoy.expedientesNuevos, color: '#7c3aed' },
              { label: 'Trámites nuevos', value: actividadHoy.tramitesNuevos, color: '#f59e0b' },
              { label: 'Eventos agendados', value: actividadHoy.eventosCreados, color: '#0d9488' },
              { label: 'Audiencias realizadas', value: actividadHoy.audienciasRealizadas, color: '#ef4444' },
            ].map(t => (
              <div key={t.label} style={{ background: 'var(--surface)', padding: '14px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: t.color, fontFamily: 'DM Mono' }}>{t.value}</div>
                <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 4 }}>{t.label}</div>
              </div>
            ))}
          </div>
        </div>

        <ProductividadPanel />

        {/* EVENTOS HOY */}
        {eventosHoy.length > 0 && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <span className="card-title">📋 Hoy</span>
              <button className="btn btn-ghost btn-xs" onClick={() => onNavigate('agenda')}>Ver agenda →</button>
            </div>
            {eventosHoy.map(ev => (
              <div key={ev.id} onClick={() => ev.expediente_id && setVerExpediente(ev.expediente_id)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 18px', borderBottom: '0.5px solid var(--border)', borderLeft: `3px solid ${colorTipo(ev.tipo)}`, cursor: ev.expediente_id ? 'pointer' : 'default' }}>
                <div style={{ fontFamily: 'DM Mono', fontSize: 12, color: colorTipo(ev.tipo), minWidth: 44 }}>{ev.hora?.substring(0, 5) || '—'}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{ev.titulo}</div>
                  <div style={{ fontSize: 11, color: 'var(--text4)' }}>{ev.tipo}{ev.clientes ? ` · ${ev.clientes.nombre} ${ev.clientes.apellido}` : ''}{ev.expedientes?.caratula ? ` · ${ev.expedientes.caratula}` : ''}</div>
                </div>
                <span className="badge badge-gray" style={{ fontSize: 10 }}>{ev.estado}</span>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          {/* PROXIMOS EVENTOS */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">📅 Próximos eventos</span>
              <button className="btn btn-ghost btn-xs" onClick={() => onNavigate('agenda')}>Ver todos →</button>
            </div>
            {proximosEventos.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text4)', fontSize: 13 }}>Sin eventos próximos</div>
            ) : proximosEventos.map(ev => (
              <div key={ev.id} onClick={() => ev.expediente_id && setVerExpediente(ev.expediente_id)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '0.5px solid var(--border)', cursor: ev.expediente_id ? 'pointer' : 'default' }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: colorTipo(ev.tipo), flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{ev.titulo}</div>
                  <div style={{ fontSize: 11, color: 'var(--text4)' }}>{formatDate(ev.fecha)}{ev.hora ? ` · ${ev.hora.substring(0, 5)}` : ''}{ev.expedientes?.caratula ? ` · ${ev.expedientes.caratula}` : ''}</div>
                </div>
              </div>
            ))}
          </div>

          {/* TAREAS PENDIENTES */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">⏳ Tareas pendientes</span>
              <button className="btn btn-ghost btn-xs" onClick={() => onNavigate('agenda')}>Ver todas →</button>
            </div>
            {tareasPendientes.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text4)', fontSize: 13 }}>Sin tareas pendientes</div>
            ) : <div style={{ maxHeight: 210, overflowY: 'auto' }}>{tareasPendientes.map(t => {
              const vencida = t.fecha_vencimiento && t.fecha_vencimiento < today;
              return (
                <div key={t.id} onClick={() => t.expediente_id && setVerExpediente(t.expediente_id)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '0.5px solid var(--border)', borderLeft: `3px solid ${t.prioridad === 'urgente' ? 'var(--red)' : 'var(--blue)'}`, cursor: t.expediente_id ? 'pointer' : 'default' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{t.titulo}</div>
                    <div style={{ fontSize: 11, color: vencida ? 'var(--red)' : 'var(--text4)' }}>
                      {t.expedientes?.caratula?.substring(0, 28) || t.tramites?.nombre || '—'}
                      {t.fecha_vencimiento && ` · ${formatDate(t.fecha_vencimiento)}`}
                    </div>
                  </div>
                  {t.prioridad === 'urgente' && <span className="badge badge-red" style={{ fontSize: 10 }}>Urgente</span>}
                </div>
              );
            })}</div>}
          </div>
        </div>

        {/* PANEL CONTABLE */}
        <div className="card">
          <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => setContableOpen(!contableOpen)}>
            <span className="card-title">Resumen contable — {new Date().toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })}</span>
            <span style={{ color: 'var(--text4)', fontSize: 14 }}>{contableOpen ? '▲' : '▼'}</span>
          </div>
          {contableOpen && (
            <div style={{ padding: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
                {[
                  { label: 'Ingresos del mes', value: formatMoney(stats.ingresosMes), color: 'var(--green)', accent: 'var(--green)' },
                  { label: 'Gastos del mes', value: formatMoney(stats.gastosMes), color: 'var(--red)', accent: 'var(--red)' },
                  { label: 'Resultado neto', value: formatMoney(stats.ingresosMes - stats.gastosMes), color: stats.ingresosMes >= stats.gastosMes ? 'var(--green)' : 'var(--red)', accent: stats.ingresosMes >= stats.gastosMes ? 'var(--green)' : 'var(--red)' },
                ].map(s => (
                  <div key={s.label} style={{ background: 'var(--surface2)', borderRadius: 8, padding: 16, borderTop: `3px solid ${s.accent}`, border: '0.5px solid var(--border)' }}>
                    <div style={{ fontSize: 11, color: 'var(--text4)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{s.label}</div>
                    <div style={{ fontSize: 22, fontWeight: 600, color: s.color }}>{s.value}</div>
                  </div>
                ))}
              </div>

              {/* GRAFICO */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, color: 'var(--text4)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>Ingresos últimos 6 meses</div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 90 }}>
                  {ingresosPorMes.map((m, i) => (
                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <div style={{ fontSize: 10, color: 'var(--text4)' }}>{formatMoney(m.total)}</div>
                      <div style={{ width: '100%', background: i === ingresosPorMes.length - 1 ? 'var(--blue)' : 'var(--blue-light)', borderRadius: '4px 4px 0 0', height: `${Math.max((m.total / maxIngreso) * 60, m.total > 0 ? 4 : 0)}px`, border: `1px solid ${i === ingresosPorMes.length - 1 ? 'var(--blue)' : '#bfdbfe'}` }} />
                      <div style={{ fontSize: 10, color: 'var(--text4)' }}>{m.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* DEUDAS DEL MES */}
              {deudasMes.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text4)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Deudas con vencimiento este mes</div>
                  {deudasMes.map(d => {
                    const saldo = (d.monto_total || 0) - (d.monto_cobrado || 0);
                    const nombre = d.clientes ? `${d.clientes.nombre} ${d.clientes.apellido}` : d.deudor_nombre || '—';
                    return (
                      <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '0.5px solid var(--border)' }}>
                        <div>
                          <div style={{ fontSize: 13, color: 'var(--text)' }}>{nombre}</div>
                          <div style={{ fontSize: 11, color: 'var(--text4)' }}>{d.concepto} · vence {formatDate(d.fecha_vencimiento)}</div>
                        </div>
                        <div style={{ fontFamily: 'DM Mono', fontSize: 13, fontWeight: 600, color: 'var(--amber)' }}>{formatMoney(saldo)}</div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => onNavigate('ingresos')}>Ver ingresos</button>
                <button className="btn btn-ghost btn-sm" onClick={() => onNavigate('gastos')}>Ver gastos</button>
                <button className="btn btn-ghost btn-sm" onClick={() => onNavigate('deudas')}>Ver deudas</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {verExpediente && (
        <>
          <div className="drawer-overlay" onMouseDown={() => setVerExpediente(null)} />
          <div className="drawer">
            <ExpedienteDetalle expedienteId={verExpediente} onClose={() => setVerExpediente(null)} backLabel="← Volver al panel" />
          </div>
        </>
      )}
    </div>
  );
}